<template>
  <div class="text-area-container" @click="focusTextarea(name)">
    <textarea
      :id="name"
      v-model="value"
      v-bind="{ ...$attrs, ...props }"
      class="text-area-input"
      :class="textareaStateClasses"
      :data-test="testAttribute"
      :disabled="disabled"
      :placeholder="placeholder"
      :rows="rows"
      @focus="handleFocus"
      @blur="handleBlur(value)"
      @input="$emit('on-input', value)"
      @change="$emit('on-change', value)"
    ></textarea>

    <p v-if="maxLength" class="text-area-char-count">{{ charCount }} / {{ maxLength }}</p>

    <label
      :for="name"
      class="text-area-label"
      :class="{ 'f-body-12': isFocused || isFilled, 'f-utility-14': !isFocused && !isFilled }"
      v-text="label"
    />

    <p-loading
      v-if="loading"
      size="sm"
      :class="assistiveText && 'mb-20'"
      class="text-area-loading"
    />

    <p
      v-if="assistiveText"
      :class="isError && 't-fg-danger'"
      class="f-utility-14 t-fg-tertiary"
      v-text="assistiveText"
    />

    <p-error-message v-if="errorState && !disabled" :name="name" class="text-area--error" />
  </div>
</template>

<script setup lang="ts">
import type { InputHTMLAttributes } from 'vue'
import { useField, ErrorMessage as PErrorMessage } from 'vee-validate'

const emit = defineEmits<{
  'on-change': [value: any]
  'on-input': [value: any]
  'on-focus': [value: boolean]
  'on-blur': [value: string]
}>()

interface State {
  onFocus: boolean
  onBlur: boolean
}

const state = reactive<State>({
  onFocus: false,
  onBlur: false,
})

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, required: true },
  placeholder: { type: String, default: '' },
  modelValue: { type: String, default: '' },
  rows: { type: Number, default: 3 },
  disabled: { type: Boolean, default: false },
  assistiveText: { type: String, default: '' },
  maxLength: { type: Number, default: 0 },
  loading: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
})

const testAttribute = dataTest(props.name as string)
const name = toRef(props, 'name')
const modelValue = toRef(props, 'modelValue')

const { value, meta, validate } = useField(name, undefined, {
  initialValue: modelValue.value,
})

const isFocused = computed(() => state.onFocus)
const isFilled = computed(() => value.value.length > 0)
const isError = computed(() => props.error || (!meta.valid && meta.validated))
const errorState = computed(() => isError.value)
const charCount = computed(() => value.value.length)

const textareaStateClasses = computed(() => {
  return [
    isFocused.value ? 'text-area-input-focus-in' : 'text-area-input-focus-out',
    isError.value && 'text-area-input-error',
    props.disabled && 'text-area-input-disabled',
  ]
})

const focusTextarea = (name: string) => {
  const el = document.getElementById(name) as HTMLElement
  el?.focus()
}

const handleFocus = () => {
  state.onFocus = true
  state.onBlur = false
  emit('on-focus', true)
}

const handleBlur = async (val: string) => {
  state.onBlur = true
  state.onFocus = false
  await validate()
  emit('on-blur', val)
}

watch(
  () => modelValue.value,
  () => (value.value = modelValue.value),
)
</script>
